import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
// Кэш для blob URL чтобы не загружать повторно
const customEmojiCache = new Map<string, string>();

interface CustomEmojiProps {
  customEmojiId: string;
  className?: string;
  title?: string;
}

/**
 * Компонент для отображения кастомного эмодзи (документа из Telegram)
 * Загружает эмодзи с сервера и кэширует результаты
 */
export function CustomEmoji({ customEmojiId, className = 'w-4 h-4', title }: CustomEmojiProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [_error, setError] = useState(false);

  useEffect(() => {
    if (!customEmojiId) {
      setError(true);
      return;
    }

    // Проверяем кэш в памяти
    if (customEmojiCache.has(customEmojiId)) {
      setImageUrl(customEmojiCache.get(customEmojiId) || null);
      return;
    }

    const loadCustomEmoji = async () => {
      try {
        setIsLoading(true);
        setError(false);

        // Загружаем эмодзи с сервера
        const response = await fetch(
          `${API_URL}/api/telegram-client/custom-emoji/${customEmojiId}`
        );

        if (!response.ok) {
          throw new Error(`Failed to load custom emoji: ${response.status}`);
        }

        // Создаем blob URL для отображения
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Кэшируем blob URL в памяти
        customEmojiCache.set(customEmojiId, blobUrl);
        setImageUrl(blobUrl);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomEmoji();
  }, [customEmojiId]);

  if (isLoading) {
    return (
      <div className={`inline-flex items-center justify-center ${className} rounded`} title={title}>
        <Loader2 className="h-3 w-3 animate-spin" />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt="Custom emoji"
        className={`inline-block ${className} rounded object-contain`}
        title={title}
      />
    );
  }

  // Плейсхолдер для кастомного эмодзи
  // Используем стиль, похожий на стикер Telegram
  return (
    <div
      className={`inline-block ${className} bg-gradient-to-br from-yellow-400 to-orange-500 rounded transition-transform hover:scale-110`}
      title={title || 'Пользовательский эмодзи'}
    />
  );
}
